import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, PaperPlaneTilt } from "@phosphor-icons/react";

export default function EmailComposer({ lead, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!lead) return;
    api.get(`/leads/${lead.id}/preview-email`).then((r) => {
      setPreview(r.data);
      setTo(r.data.to || "");
      setSubject(r.data.subject || "");
      setBody(r.data.body || "");
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to load preview"));
  }, [lead]);

  const send = async () => {
    if (!to) return toast.error("Recipient required");
    setSending(true);
    try {
      await api.post(`/leads/${lead.id}/email`, { to, cc: cc || null, subject, body });
      toast.success(`Email sent to ${to}`);
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!lead) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-modal">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border p-4 flex items-center justify-between">
          <div>
            <div className="overline-label text-muted-foreground">Send outreach email</div>
            <div className="font-heading font-bold text-base mt-0.5">{lead.app_name}</div>
          </div>
          <button onClick={onClose} data-testid="btn-close-email"
                  className="p-1.5 hover:bg-accent rounded-sm"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="overline-label text-muted-foreground mb-1">To</div>
            <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-to"
                   className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">CC (optional)</div>
            <input value={cc} onChange={(e) => setCc(e.target.value)}
                   className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Subject</div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject"
                   className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm" />
          </div>
          <div>
            <div className="overline-label text-muted-foreground mb-1">Body</div>
            <textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} data-testid="email-body"
                      className="w-full border border-input bg-background px-3 py-2 text-sm rounded-sm font-mono" />
          </div>
          {preview && (
            <div className="text-[11px] text-muted-foreground border-t border-border pt-2">
              Rendered from admin template · placeholders auto-filled from lead + app data.
            </div>
          )}
        </div>
        <div className="border-t border-border p-3 flex justify-end gap-2">
          <button onClick={onClose} className="border border-border px-3 py-2 text-sm rounded-sm hover:bg-accent">Cancel</button>
          <button data-testid="btn-send-email" disabled={sending || !to} onClick={send}
                  className="bg-primary text-primary-foreground px-4 py-2 text-sm rounded-sm flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50">
            <PaperPlaneTilt size={14} /> {sending ? "Sending…" : "Send email"}
          </button>
        </div>
      </div>
    </div>
  );
}
